import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Flag, Images, Lock, Play, Wand2 } from 'lucide-react';
import { CONSENT_COPY, DAILY_LIFE_COPY, EMPTY_STATES, ROLES, isVideoMime } from '@saesak/shared';
import { AppBar, ChipRow, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { InstaEditor } from '@/components/InstaEditor';
import { ReportSheet } from '@/components/SafetyActions';
import { api, apiData } from '@/lib/api';
import { useMediaUrl } from '@/lib/media-url';
import { formatDate } from '@/lib/format';
import { useAuth } from '@/store/auth';
import type { Album, ChildSummary } from '@/types';

/** 앨범 목록 — 명세서 §5.5. 아이별 필터 + 활동일 묶음으로 본다. */
export function AlbumListPage() {
  const membership = useAuth((s) => s.membership);
  const isParent = membership?.role === ROLES.PARENT;
  const [childId, setChildId] = useState('');

  // 학부모는 자녀, 교직원은 담당 원아가 내려온다 — 아이별 필터 칩
  const childrenQuery = useQuery({
    queryKey: ['children'],
    queryFn: () => apiData<ChildSummary[]>('/children'),
  });
  const children = childrenQuery.data ?? [];
  const selectedChild = children.find((child) => child.id === childId);

  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['albums', childId],
      initialPageParam: undefined as string | undefined,
      queryFn: ({ pageParam }) =>
        api<Album[]>(
          `/albums?limit=30${childId ? `&childId=${childId}` : ''}${pageParam ? `&cursor=${pageParam}` : ''}`,
        ),
      getNextPageParam: (last) => last.meta?.nextCursor ?? undefined,
      // 아이 필터를 바꿔도 직전 목록을 그대로 둔 채 갱신 — 화면이 스피너로 비지 않는다
      placeholderData: keepPreviousData,
    });

  const albums = data?.pages.flatMap((page) => page.data) ?? [];

  // 활동일 기준 일자별 묶음 — 서버가 이미 활동일 내림차순으로 준다
  const groups: { label: string; albums: Album[] }[] = [];
  for (const album of albums) {
    const label = formatDate(album.activityDate ?? album.publishedAt ?? album.createdAt);
    // `.at(-1)`은 옛 WebView(Chrome 92 미만)에 없어 화면이 죽었다 — 인덱스 접근으로 둔다
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.albums.push(album);
    else groups.push({ label, albums: [album] });
  }

  return (
    <Screen>
      <AppBar
        title="앨범"
        back
        right={
          !isParent && (
            <Link to="/records/albums/new" className="text-sm font-semibold text-primary-strong">
              새 앨범
            </Link>
          )
        }
      />

      {isParent && (
        <p className="px-4 pt-3 text-sm text-ink-sub">{DAILY_LIFE_COPY.album.parentScopeNotice}</p>
      )}

      {children.length > 1 && (
        <div className="px-4 pt-3">
          <ChipRow>
            <Chip tone={!childId ? 'primary' : 'neutral'} selected={!childId} onClick={() => setChildId('')}>
              전체
            </Chip>
            {children.map((child) => (
              <Chip
                key={child.id}
                tone={childId === child.id ? 'primary' : 'neutral'}
                selected={childId === child.id}
                onClick={() => setChildId(child.id)}
              >
                {child.name}
              </Chip>
            ))}
          </ChipRow>
        </div>
      )}

      {isPending && <Spinner />}
      {isError && <ErrorNote message="앨범을 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && albums.length === 0 && (
        <EmptyState
          title={EMPTY_STATES.album.title}
          body={
            selectedChild
              ? DAILY_LIFE_COPY.album.filteredEmpty(selectedChild.name)
              : EMPTY_STATES.album.body
          }
        />
      )}

      <div className="flex flex-col gap-3 p-4">
        {groups.map((group) => (
          <section key={group.label} className="pb-2">
            <h2 className="pb-2 text-sm font-semibold text-ink-sub">{group.label}</h2>
            <ul className="grid grid-cols-2 gap-3">
              {group.albums.map((album) => (
                <Card key={album.id} as="li" className="p-0">
                  <Link to={`/album/${album.id}`} className="block">
                    <AlbumCover album={album} />
                    <div className="p-3">
                      <p className="truncate font-semibold">{album.title}</p>
                      <p className="text-sm text-ink-sub">
                        {album.classRoom ? `${album.classRoom.name} · ` : ''}
                        {DAILY_LIFE_COPY.album.mediaCount(
                          album.photos.filter((photo) => !isVideoMime(photo.media.mimeType)).length,
                          album.photos.filter((photo) => isVideoMime(photo.media.mimeType)).length,
                        )}
                      </p>
                      {album.status !== 'PUBLISHED' && (
                        <Chip tone="neutral" className="mt-1.5">
                          임시저장
                        </Chip>
                      )}
                    </div>
                  </Link>
                </Card>
              ))}
            </ul>
          </section>
        ))}
      </div>

      {hasNextPage && (
        <div className="px-4 pb-6">
          <Button
            variant="outline"
            block
            loading={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            더 보기
          </Button>
        </div>
      )}
    </Screen>
  );
}

/** 앨범 상세 — 사진 격자 + 원본 저장 정책 안내 */
export function AlbumDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [zoomed, setZoomed] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [reporting, setReporting] = useState<string | null>(null);
  const membership = useAuth((s) => s.membership);
  const isStaff = membership?.role !== ROLES.PARENT;

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['albums', id],
    queryFn: () => apiData<Album>(`/albums/${id}`),
    enabled: Boolean(id),
  });

  return (
    <Screen withNav={false}>
      <AppBar title={data?.title ?? '앨범'} back />

      {isPending && <Spinner />}
      {isError && <ErrorNote message="앨범을 불러오지 못했어요." onRetry={() => void refetch()} />}

      {data && (
        <div className="p-4">
          <p className="text-sm text-ink-sub">
            {formatDate(data.activityDate ?? data.publishedAt)}
            {data.classRoom && ` · ${data.classRoom.name}`}
          </p>
          {data.description && <p className="mt-2 text-base">{data.description}</p>}

          <div className="mt-4 grid grid-cols-3 gap-2">
            {data.photos.map((photo) => {
              const isVideo = isVideoMime(photo.media.mimeType);
              return (
              <div key={photo.id} className="relative">
                <button
                  type="button"
                  onClick={() => setZoomed(photo.mediaId)}
                  className="tap block aspect-square w-full overflow-hidden rounded-sm bg-primary-soft"
                >
                  <PhotoImage mediaId={photo.mediaId} alt={photo.caption ?? ''} />
                </button>
                {/* 영상 표시 (§5.5 v1.22) — 격자에서는 첫 장면만, 눌러서 재생 */}
                {isVideo && (
                  <span className="pointer-events-none absolute right-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-surface/85 px-1.5 py-0.5 text-[11px] font-semibold text-ink">
                    <Play size={11} /> {DAILY_LIFE_COPY.album.videoBadge}
                  </span>
                )}
                {/* 안전 도구 (§5.30) — 사진 신고 */}
                <button
                  type="button"
                  aria-label="사진 신고"
                  className="tap absolute bottom-1 left-1 grid h-7 w-7 place-items-center rounded-full bg-surface/85 text-ink-sub"
                  onClick={() => setReporting(photo.id)}
                >
                  <Flag size={12} />
                </button>
                {/* 인스타 편집기 (§5.5) — 원 계정 운영용, 교직원 전용. 영상은 캔버스 편집 대상이 아니다 */}
                {isStaff && !isVideo && (
                  <button
                    type="button"
                    aria-label="인스타용 만들기"
                    className="tap absolute bottom-1 right-1 grid h-7 w-7 place-items-center rounded-full bg-surface/85 text-primary-strong"
                    onClick={() => setEditing(photo.mediaId)}
                  >
                    <Wand2 size={13} />
                  </button>
                )}
              </div>
              );
            })}
          </div>

          {/* 원본 저장 차단은 정책이므로 화면에서 미리 알려 준다 (§5.5, §14.1-9) */}
          <p className="mt-4 flex items-center gap-1.5 text-sm text-ink-sub">
            <Lock size={14} /> {CONSENT_COPY.downloadBlocked}
          </p>
        </div>
      )}

      {zoomed && (
        <div
          role="presentation"
          className="fixed inset-0 z-50 grid place-items-center bg-ink/80 p-4"
          onClick={() => setZoomed(null)}
        >
          {/* 영상은 컨트롤을 눌러야 하므로 닫기는 바깥 여백·닫기 버튼으로 */}
          <button
            type="button"
            className="absolute right-3 top-3 rounded-full bg-surface/90 px-3 py-1.5 text-sm font-semibold text-ink"
            onClick={() => setZoomed(null)}
          >
            닫기
          </button>
          <div onClick={(e) => e.stopPropagation()} role="presentation" className="max-h-full max-w-full">
            <PhotoImage mediaId={zoomed} alt="" className="max-h-[85vh] max-w-full rounded-sm" controls />
          </div>
        </div>
      )}

      {editing && <InstaEditor mediaId={editing} onClose={() => setEditing(null)} />}
      {reporting && <ReportSheet target={{ type: 'PHOTO', id: reporting }} onClose={() => setReporting(null)} />}
    </Screen>
  );
}

function AlbumCover({ album }: { album: Album }) {
  const cover = album.photos[0];
  if (!cover) {
    return (
      <div className="grid aspect-square place-items-center rounded-t-md bg-primary-soft text-primary-strong">
        <Images size={28} />
      </div>
    );
  }
  return (
    <div className="aspect-square overflow-hidden rounded-t-md bg-primary-soft">
      <PhotoImage mediaId={cover.mediaId} alt="" />
    </div>
  );
}

/**
 * 사진은 서명 URL로만 내려온다(§9.1). 만료가 짧으므로 화면에 뜰 때 발급받는다.
 * 실패해도 레이아웃이 무너지지 않게 빈 자리를 유지한다.
 */
export function PhotoImage({
  mediaId,
  alt,
  className,
  controls = false,
}: {
  mediaId: string;
  alt: string;
  className?: string;
  /** 영상일 때 재생 컨트롤을 보일지 — 격자에서는 첫 장면만, 확대 화면에서만 재생한다 (§5.5 v1.22) */
  controls?: boolean;
}) {
  const { data } = useMediaUrl(mediaId);

  if (!data?.url) return <div className={className ?? 'h-full w-full bg-primary-soft'} />;
  if (isVideoMime(data.mimeType)) {
    return (
      <video
        src={data.url}
        className={className ?? 'h-full w-full object-cover'}
        controls={controls}
        controlsList="nodownload"
        playsInline
        muted={!controls}
        preload="metadata"
        aria-label={alt}
      />
    );
  }
  return (
    <img
      src={data.url}
      alt={alt}
      className={className ?? 'h-full w-full object-cover'}
      loading="lazy"
    />
  );
}
